import { useEffect, useState } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import {
    Search, LayoutDashboard, Settings, Users,
    FileText, Wrench, LogOut,
    Calculator, User, LifeBuoy,
    Moon,
    Sun,
    MonitorSmartphone
} from 'lucide-react';
import { useVertical } from '../../context/VerticalContext';
import { supabase } from '../../lib/supabase';
import { useTheme } from '../../context/ThemeContext';

export const CommandPalette = () => {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const { molded } = useVertical();
    const { preference, setPreference } = useTheme();

    // Toggle with Cmd+K / Ctrl+K
    useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                setOpen((open) => !open);
            }
        };

        document.addEventListener('keydown', down);
        return () => document.removeEventListener('keydown', down);
    }, []);

    const runCommand = (command: () => void) => {
        setOpen(false);
        command();
    };

    if (!open) return null;

    return (
        <Command.Dialog
            open={open}
            onOpenChange={setOpen}
            label="Global Command Menu"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-scrim/50 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={(e) => {
                if (e.target === e.currentTarget) setOpen(false);
            }}
        >
            <div className="w-full max-w-2xl bg-panel border border-line rounded-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 ring-1 ring-tint/10">
                <div className="flex items-center border-b border-line px-3">
                    <Search className="w-5 h-5 text-fg-3 mr-2" />
                    <Command.Input
                        autoFocus
                        placeholder="Type a command or search..."
                        className="w-full h-14 bg-transparent outline-none text-fg placeholder-fg-3 text-lg"
                    />
                </div>

                <Command.List className="max-h-[60vh] overflow-y-auto overflow-x-hidden p-2 scroll-py-2">
                    <Command.Empty className="py-6 text-center text-fg-3 text-sm">
                        No results found.
                    </Command.Empty>

                    {/* --- NAVIGATION --- */}
                    <Command.Group heading="Navigation" className="text-fd-3xs uppercase tracking-wider text-fg-3 font-bold mb-2 px-2 mt-2">
                        <Command.Item
                            onSelect={() => runCommand(() => navigate('/'))}
                            className="flex items-center gap-2 px-2 py-3 rounded-lg text-fg aria-selected:bg-accent/10 aria-selected:text-accent cursor-pointer transition-colors"
                        >
                            <LayoutDashboard className="w-4 h-4" />
                            <span>Dashboard</span>
                        </Command.Item>

                        <Command.Item
                            onSelect={() => runCommand(() => navigate('/clients'))}
                            className="flex items-center gap-2 px-2 py-3 rounded-lg text-fg aria-selected:bg-accent/10 aria-selected:text-accent cursor-pointer transition-colors"
                        >
                            <Users className="w-4 h-4" />
                            <span>{molded.jargon.client_label}s</span>
                        </Command.Item>

                        <Command.Item
                            onSelect={() => runCommand(() => navigate('/workshop'))}
                            className="flex items-center gap-2 px-2 py-3 rounded-lg text-fg aria-selected:bg-accent/10 aria-selected:text-accent cursor-pointer transition-colors"
                        >
                            <Wrench className="w-4 h-4" />
                            <span>Workshop</span>
                        </Command.Item>

                        <Command.Item
                            onSelect={() => runCommand(() => navigate('/finance/invoices'))}
                            className="flex items-center gap-2 px-2 py-3 rounded-lg text-fg aria-selected:bg-accent/10 aria-selected:text-accent cursor-pointer transition-colors"
                        >
                            <FileText className="w-4 h-4" />
                            <span>Invoices</span>
                        </Command.Item>
                    </Command.Group>

                    {/* --- SETTINGS & ADMIN --- */}
                    <Command.Group heading="System" className="text-fd-3xs uppercase tracking-wider text-fg-3 font-bold mb-2 px-2 mt-4">
                        <Command.Item
                            keywords={['guide', 'guides', 'how to', 'tutorial', 'support', 'docs']}
                            onSelect={() => runCommand(() => navigate('/help'))}
                            className="flex items-center gap-2 px-2 py-3 rounded-lg text-fg aria-selected:bg-accent/10 aria-selected:text-accent cursor-pointer transition-colors"
                        >
                            <LifeBuoy className="w-4 h-4" />
                            <span>Help &amp; Guides</span>
                        </Command.Item>

                        <Command.Item
                            onSelect={() => runCommand(() => navigate('/settings?tab=general'))}
                            className="flex items-center gap-2 px-2 py-3 rounded-lg text-fg aria-selected:bg-accent/10 aria-selected:text-accent cursor-pointer transition-colors"
                        >
                            <Settings className="w-4 h-4" />
                            <span>Settings</span>
                        </Command.Item>

                        <Command.Item
                            onSelect={() => runCommand(() => navigate('/settings?tab=fiscal'))}
                            className="flex items-center gap-2 px-2 py-3 rounded-lg text-fg aria-selected:bg-accent/10 aria-selected:text-accent cursor-pointer transition-colors"
                        >
                            <Calculator className="w-4 h-4" />
                            <span>Tax &amp; VAT</span>
                        </Command.Item>

                        <Command.Item
                            onSelect={() => runCommand(() => navigate('/profile'))}
                            className="flex items-center gap-2 px-2 py-3 rounded-lg text-fg aria-selected:bg-accent/10 aria-selected:text-accent cursor-pointer transition-colors"
                        >
                            <User className="w-4 h-4" />
                            <span>My Profile</span>
                        </Command.Item>
                    </Command.Group>

                    {/* --- ACTIONS --- */}
                    <Command.Group heading="Actions" className="text-fd-3xs uppercase tracking-wider text-fg-3 font-bold mb-2 px-2 mt-4">
                        <Command.Item
                            onSelect={() => runCommand(() => setPreference('dark'))}
                            className="flex items-center gap-2 px-2 py-3 rounded-lg text-fg aria-selected:bg-accent/10 aria-selected:text-accent cursor-pointer transition-colors"
                        >
                            <Moon className="w-4 h-4" />
                            <span>Dark theme</span>
                            {preference === 'dark' && <span className="ml-auto text-fd-3xs text-fg-4">Current</span>}
                        </Command.Item>

                        <Command.Item
                            onSelect={() => runCommand(() => setPreference('ivory'))}
                            className="flex items-center gap-2 px-2 py-3 rounded-lg text-fg aria-selected:bg-accent/10 aria-selected:text-accent cursor-pointer transition-colors"
                        >
                            <Sun className="w-4 h-4" />
                            <span>Ivory theme</span>
                            {preference === 'ivory' && <span className="ml-auto text-fd-3xs text-fg-4">Current</span>}
                        </Command.Item>

                        <Command.Item
                            onSelect={() => runCommand(() => setPreference('system'))}
                            className="flex items-center gap-2 px-2 py-3 rounded-lg text-fg aria-selected:bg-accent/10 aria-selected:text-accent cursor-pointer transition-colors"
                        >
                            <MonitorSmartphone className="w-4 h-4" />
                            <span>Theme: match this device</span>
                            {preference === 'system' && <span className="ml-auto text-fd-3xs text-fg-4">Current</span>}
                        </Command.Item>


                        <Command.Item
                            onSelect={() => runCommand(async () => {
                                await supabase.auth.signOut();
                                navigate('/login');
                            })}
                            className="flex items-center gap-2 px-2 py-3 rounded-lg text-danger aria-selected:bg-danger/10 aria-selected:text-danger cursor-pointer transition-colors"
                        >
                            <LogOut className="w-4 h-4" />
                            <span>Log Out</span>
                        </Command.Item>
                    </Command.Group>

                </Command.List>

                <div className="px-4 py-2 border-t border-line bg-raised flex items-center justify-between text-xs text-fg-3">
                    <span className="flex items-center gap-1">
                        <kbd className="px-1.5 py-0.5 rounded bg-raised border border-line">↑</kbd>
                        <kbd className="px-1.5 py-0.5 rounded bg-raised border border-line">↓</kbd>
                        to navigate
                    </span>
                    <span className="flex items-center gap-1">
                        <kbd className="px-1.5 py-0.5 rounded bg-raised border border-line">↵</kbd>
                        to select
                    </span>
                </div>
            </div>
        </Command.Dialog>
    );
};
